import React, { useState, useEffect } from 'react';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
    AlertTriangle,
    Bell,
    CheckCircle,
    XCircle,
    RefreshCw,
    Filter,
    Truck,
    Gauge,
    Clock,
    Eye,
    Info,
    AlertOctagon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_ICONS = {
    rendimiento_bajo: Gauge,
    rendimiento_alto: Gauge,
    km_inconsistente: AlertOctagon,
    carga_excesiva: AlertTriangle,
    licencia_vencida: Clock,
};

const TIPO_LABELS = {
    rendimiento_bajo: 'Rendimiento Bajo',
    rendimiento_alto: 'Rendimiento Alto',
    km_inconsistente: 'KM Inconsistente',
    carga_excesiva: 'Carga Excesiva',
    licencia_vencida: 'Licencia Vencida',
};

const SEVERIDAD_CONFIG = {
    informacion: { color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
    advertencia: { color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
    critico: { color: 'bg-red-500', text: 'text-red-600', bg: 'bg-red-500/10', border: 'border-red-500/30' },
};

export default function DieselAlertasPage() {
    const { hasPermission } = useDiesel();
    const [alertas, setAlertas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [estadoFilter, setEstadoFilter] = useState('pendiente');
    const [tipoFilter, setTipoFilter] = useState('');
    const [dialogResolver, setDialogResolver] = useState({ open: false, alerta: null });
    const [observacion, setObservacion] = useState('');
    const [processing, setProcessing] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        loadAlertas();
    }, [estadoFilter, tipoFilter]);

    const loadAlertas = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/diesel/alertas?limit=200`;
            if (estadoFilter && estadoFilter !== 'all') url += `&estado=${estadoFilter}`;
            if (tipoFilter && tipoFilter !== 'all') url += `&tipo=${tipoFilter}`;

            const res = await fetch(url, { headers });
            if (res.ok) setAlertas(await res.json());
        } catch (err) {
            toast.error('Error al cargar alertas');
        } finally {
            setLoading(false);
        }
    };

    const handleResolver = async (accion) => {
        if (!dialogResolver.alerta) return;

        setProcessing(true);
        try {
            const endpoint = accion === 'resolver' ? 'resolver' : 'ignorar';
            const res = await fetch(`${API_URL}/api/diesel/alertas/${dialogResolver.alerta.id}/${endpoint}`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ observacion }),
            });

            if (res.ok) {
                toast.success(accion === 'resolver' ? 'Alerta resuelta' : 'Alerta ignorada');
                setDialogResolver({ open: false, alerta: null });
                setObservacion('');
                loadAlertas();
            }
        } catch (err) {
            toast.error('Error al procesar alerta');
        } finally {
            setProcessing(false);
        }
    };

    const getResumenAlertas = () => {
        const pendientes = alertas.filter(a => a.estado === 'pendiente');
        return {
            total: pendientes.length,
            criticas: pendientes.filter(a => a.severidad === 'critico').length,
            advertencias: pendientes.filter(a => a.severidad === 'advertencia').length,
            info: pendientes.filter(a => a.severidad === 'informacion').length,
        };
    };

    const resumen = getResumenAlertas();

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-3">
                            <Bell className="h-8 w-8" style={{ color: 'var(--diesel-primary)' }} />
                            Centro de Alertas
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {alertas.length} alerta{alertas.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <Button variant="outline" onClick={loadAlertas}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                </div>

                {/* Resumen de alertas pendientes */}
                {estadoFilter === 'pendiente' && resumen.total > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="border-2">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-black">{resumen.total}</p>
                                <p className="text-xs text-muted-foreground uppercase">Total Pendientes</p>
                            </CardContent>
                        </Card>
                        <Card className="border-2 border-red-500/30 bg-red-500/5">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-black text-red-600">{resumen.criticas}</p>
                                <p className="text-xs text-muted-foreground uppercase">Críticas</p>
                            </CardContent>
                        </Card>
                        <Card className="border-2 border-amber-500/30 bg-amber-500/5">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-black text-amber-600">{resumen.advertencias}</p>
                                <p className="text-xs text-muted-foreground uppercase">Advertencias</p>
                            </CardContent>
                        </Card>
                        <Card className="border-2 border-blue-500/30 bg-blue-500/5">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-black text-blue-600">{resumen.info}</p>
                                <p className="text-xs text-muted-foreground uppercase">Información</p>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Filtros */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 space-y-2">
                                <Label className="text-xs uppercase text-muted-foreground">Estado</Label>
                                <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pendiente">Pendientes</SelectItem>
                                        <SelectItem value="resuelta">Resueltas</SelectItem>
                                        <SelectItem value="ignorada">Ignoradas</SelectItem>
                                        <SelectItem value="all">Todas</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex-1 space-y-2">
                                <Label className="text-xs uppercase text-muted-foreground">Tipo</Label>
                                <Select value={tipoFilter} onValueChange={setTipoFilter}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Todos los tipos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        <SelectItem value="rendimiento_bajo">Rendimiento Bajo</SelectItem>
                                        <SelectItem value="rendimiento_alto">Rendimiento Alto</SelectItem>
                                        <SelectItem value="km_inconsistente">KM Inconsistente</SelectItem>
                                        <SelectItem value="carga_excesiva">Carga Excesiva</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Lista de alertas */}
                <div className="space-y-4">
                    {loading ? (
                        [...Array(5)].map((_, i) => (
                            <Skeleton key={i} className="h-32" />
                        ))
                    ) : alertas.length === 0 ? (
                        <Card className="border-2">
                            <CardContent className="py-16">
                                <div className="text-center">
                                    <CheckCircle className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
                                    <p className="text-xl font-bold">Sin alertas</p>
                                    <p className="text-muted-foreground">
                                        {estadoFilter === 'pendiente' 
                                            ? 'No hay alertas pendientes de revisión'
                                            : 'No hay alertas con los filtros aplicados'}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        alertas.map((alerta) => {
                            const config = SEVERIDAD_CONFIG[alerta.severidad] || SEVERIDAD_CONFIG.advertencia;
                            const Icon = TIPO_ICONS[alerta.tipo] || AlertTriangle;

                            return (
                                <Card 
                                    key={alerta.id} 
                                    className={`border-2 ${config.border} ${config.bg}`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-4">
                                            <div className={`p-3 rounded-lg ${config.bg}`}>
                                                <Icon className={`h-6 w-6 ${config.text}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Badge className={config.color}>
                                                        {alerta.severidad}
                                                    </Badge>
                                                    <Badge variant="outline">
                                                        {TIPO_LABELS[alerta.tipo] || alerta.tipo}
                                                    </Badge>
                                                    {alerta.estado !== 'pendiente' && (
                                                        <Badge variant="secondary">
                                                            {alerta.estado}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="font-medium mb-2">{alerta.mensaje}</p>
                                                
                                                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                                    {alerta.vehiculo_info && (
                                                        <Link
                                                            to={`/diesel/vehiculos/${alerta.vehiculo_id}`}
                                                            className="flex items-center gap-1 hover:underline hover:text-foreground transition-colors"
                                                            data-testid={`alerta-vehiculo-link-${alerta.id}`}
                                                        >
                                                            <Truck className="h-4 w-4" />
                                                            {alerta.vehiculo_info.patente}
                                                        </Link>
                                                    )}
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="h-4 w-4" />
                                                        {new Date(alerta.created_at).toLocaleString()}
                                                    </span>
                                                </div>

                                                {/* Detalles expandibles */}
                                                {alerta.detalles && Object.keys(alerta.detalles).length > 0 && (
                                                    <div className="mt-3 p-3 bg-background/50 rounded-lg text-sm">
                                                        <p className="font-medium mb-1">Detalles:</p>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {Object.entries(alerta.detalles).map(([key, value]) => (
                                                                <div key={key}>
                                                                    <span className="text-muted-foreground">
                                                                        {key.replace(/_/g, ' ')}:
                                                                    </span>{' '}
                                                                    <span className="font-mono">
                                                                        {typeof value === 'number' ? value.toLocaleString() : value}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Info de resolución */}
                                                {alerta.estado !== 'pendiente' && alerta.resuelta_por_nombre && (
                                                    <div className="mt-3 p-3 bg-muted/50 rounded-lg text-sm">
                                                        <p>
                                                            <span className="text-muted-foreground">Resuelta por:</span>{' '}
                                                            {alerta.resuelta_por_nombre}
                                                        </p>
                                                        {alerta.observacion_resolucion && (
                                                            <p className="mt-1 italic">"{alerta.observacion_resolucion}"</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Acciones */}
                                            {alerta.estado === 'pendiente' && hasPermission('resolver_alertas') && (
                                                <div className="flex flex-col gap-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setDialogResolver({ open: true, alerta })}
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                    >
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                        Resolver
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setDialogResolver({ open: true, alerta, accion: 'ignorar' });
                                                        }}
                                                    >
                                                        <XCircle className="h-4 w-4 mr-1" />
                                                        Ignorar
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Dialog Resolver/Ignorar */}
            <Dialog open={dialogResolver.open} onOpenChange={(open) => !open && setDialogResolver({ open: false, alerta: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {dialogResolver.accion === 'ignorar' ? 'Ignorar Alerta' : 'Resolver Alerta'}
                        </DialogTitle>
                        <DialogDescription>
                            {dialogResolver.alerta?.mensaje}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="space-y-2">
                            <Label>Observación {dialogResolver.accion === 'ignorar' ? '(requerida)' : '(opcional)'}</Label>
                            <Textarea
                                value={observacion}
                                onChange={(e) => setObservacion(e.target.value)}
                                placeholder={
                                    dialogResolver.accion === 'ignorar'
                                        ? 'Explique por qué se ignora esta alerta...'
                                        : 'Describa las acciones tomadas...'
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogResolver({ open: false, alerta: null })}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => handleResolver(dialogResolver.accion === 'ignorar' ? 'ignorar' : 'resolver')}
                            disabled={processing || (dialogResolver.accion === 'ignorar' && !observacion.trim())}
                            className={dialogResolver.accion === 'ignorar' ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                        >
                            {processing ? 'Procesando...' : dialogResolver.accion === 'ignorar' ? 'Ignorar' : 'Resolver'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DieselLayout>
    );
}
